import { Icon } from '@/icons'
import type { MarketplaceResult } from '@/lib/api'

const rupees = (value: number) => `₹${value.toLocaleString('en-IN')}`

/** The business's logo, or its initials on its own colour. */
export function Avatar({ business }: { business: MarketplaceResult }) {
  if (business.logoUrl) {
    return (
      <span className="av">
        <img src={business.logoUrl} alt="" loading="lazy" />
      </span>
    )
  }
  const initials =
    (business.name.match(/\b\p{L}/gu) ?? []).slice(0, 2).join('').toUpperCase() || 'B'
  const style = business.accent
    ? { background: business.accent, color: business.accentInk ?? '#fff' }
    : undefined
  return (
    <span className="av" style={style} aria-hidden="true">
      {initials}
    </span>
  )
}

function Price({ business }: { business: MarketplaceResult }) {
  const service = business.service
  if (service?.feeInr != null) {
    return (
      <p>
        {service.name} · <strong>{rupees(service.feeInr)}</strong>
      </p>
    )
  }
  if (service) return <p>{service.name}</p>
  if (business.fromInr != null) return <p>From <strong>{rupees(business.fromInr)}</strong></p>
  return <p className="mute">Pricing on profile</p>
}

/**
 * One result. Every link goes to the business's own Blooq app: the marketplace
 * finds, the app books. A time shown here is the business's next free slot as
 * of this search, and the app re-reads it before anyone can take it.
 */
export function ResultCard({ business }: { business: MarketplaceResult }) {
  const where = [business.industry, business.address].filter(Boolean).join(' · ')
  const availability = business.availability

  return (
    <article className="res">
      <Avatar business={business} />
      <h3>{business.name}</h3>
      {where && <p className="mute">{where}</p>}
      <div className="grow">
        <div style={{ marginTop: 8 }}>
          <Price business={business} />
        </div>
        {!business.acceptingBookings ? (
          <p className="mute">Not taking bookings right now</p>
        ) : availability && availability.slots.length > 0 ? (
          <>
            <p className="mute">{availability.dayLabel}</p>
            <div className="chips">
              {availability.slots.map((slot) => (
                <a key={slot.at} className="chip" href={business.url} target="_blank" rel="noopener">
                  {slot.label}
                </a>
              ))}
            </div>
          </>
        ) : availability ? (
          <p className="mute">No free times {availability.dayLabel.toLowerCase()}</p>
        ) : (
          business.tagline && <p className="mute" style={{ marginTop: 6 }}>{business.tagline}</p>
        )}
      </div>
      <a className="btn" href={business.url} target="_blank" rel="noopener">
        {business.acceptingBookings ? 'View & Book' : 'View business'}
      </a>
    </article>
  )
}

/** The smaller card in "Discover businesses near you". */
export function NearCard({ business }: { business: MarketplaceResult }) {
  return (
    <article className="card near">
      <Avatar business={business} />
      <h3>{business.name}</h3>
      <p className="mute">{[business.industry, business.address].filter(Boolean).join(' · ')}</p>
      <p className="mute grow">
        {business.fromInr != null ? `From ${rupees(business.fromInr)}` : 'Pricing on profile'}
      </p>
      <a className="more" href={business.url} target="_blank" rel="noopener">
        Open their app <Icon name="arrow" size={15} />
      </a>
    </article>
  )
}
